import {
  FontPreviewText,
  FontPreviewTextProps,
  getFontFamilyName,
} from "pages/media/brandkit/components/FontPreview";
import { useTextLineHeightPx } from "../../../../hooks/useTextLineHeightPx";
import {
  CommonTextProperties,
  NullableHeightSize,
  Size,
} from "../../../../types";
import { useOne, useTranslate } from "@refinedev/core";
import {
  CUSTOM_TEXT_RELATIVE_PADDING,
  DEFAULT_RELATIVE_PADDING,
} from "../../../../constants";
import { forwardRef, RefObject, useMemo } from "react";
import { TextAreaRef } from "antd/es/input/TextArea";
import { MediaAsset } from "pages/media/types";
import {
  ASSET_REFRESH_INTERVAL,
  ASSET_STALE_TIME,
} from "pages/media/constants";
import { getFontSizeInPixels } from "../../../../helpers";
import {
  mapRelativeToPixels,
  resolveColorToBrandkitColorIfNeeded,
  resolveToBrandkitFontIfNeeded,
} from "../../../../helpers/itemsHelper";
import { useTemplateData } from "../../../../hooks/useTemplateData";

const VERTICAL_ALIGN_MAP = {
  top: "flex-start",
  center: "center",
  bottom: "flex-end",
};

export interface TextProps extends Omit<FontPreviewTextProps, "font"> {
  textFragment?: string;
  textItem: Omit<CommonTextProperties, "size"> & {
    content?: string;
    animated?: boolean;
    size: Size | NullableHeightSize;
  };
  previewWidth: number;
  previewHeight: number;
  styles?: React.CSSProperties;
  /**
   * Fill the parent (width/height 100%) instead of using the item's pixel size.
   * Used in the editor so the text follows the Moveable box during a live resize
   * (and its alignment re-positions the content / background), the way a shape's
   * 100%-sized renderer does. Off elsewhere (the item keeps its own box size).
   */
  fillContainer?: boolean;
}

export const Text = forwardRef<TextAreaRef, TextProps>(
  (
    {
      textFragment,
      textItem,
      styles,
      previewWidth,
      previewHeight,
      fillContainer,
      ...props
    }: TextProps,
    ref
  ) => {
    const t = useTranslate();

    const {
      brandKitData: { brandKitPalette, titleFont, subtitlesFont, textFont },
      placeholderAssets,
    } = useTemplateData();

    const fontAssetId = resolveToBrandkitFontIfNeeded(
      textItem.fontAssetId,
      titleFont,
      subtitlesFont,
      textFont
    );

    const placeholderFontPath = fontAssetId
      ? placeholderAssets?.[fontAssetId]
      : undefined;

    const { data: fontAsset, isFetched } = useOne<MediaAsset>({
      resource: `media/media/assets`,
      id: fontAssetId,
      queryOptions: {
        // fetch new asset even if we have fontPath
        // we had weird stale data when adding fontPath condition in enabled
        // exception: a blob: fontPath means the asset was just uploaded and
        // isn't backend-ready yet, so fetching it now would just 404
        enabled:
          !!fontAssetId &&
          !placeholderFontPath &&
          !textItem.fontPath?.startsWith("blob:"),
        staleTime: ASSET_STALE_TIME,
        refetchInterval: ASSET_REFRESH_INTERVAL,
      },
    });

    // fontPath is filled when switching font from the font picker (toolbar use case)
    // try to use it first, to reuse the font face from cache
    // warning: font select will return different font path for the same asset id because of different signed urls
    const fontPath =
      textItem.fontPath ?? placeholderFontPath ?? fontAsset?.data.path;

    const previewText = useMemo(() => {
      return (
        textFragment ??
        textItem.content ??
        t("media.brandkit.mockup.exampleText")
      );
    }, [t, textFragment, textItem.content]);

    // Match the server's line spacing (which is based on the glyph ink bbox,
    // not the em) by measuring the same box and setting line-height in px.
    // Falls back to the em-based multiplier until the font is loaded.
    const fontFamilyName = useMemo(
      () => (fontPath ? getFontFamilyName(fontPath) : ""),
      [fontPath]
    );

    const isSubtitlesOrTitleItem =
      "showSubtitles" in textItem || "showTitle" in textItem;
    const hasBackground = !!textItem.backgroundColor;

    // The font always renders at its set size — when `adjustFont` is on the box
    // grows to fit the font (handled in TextEditable), rather than the font
    // shrinking to fit the box.
    const fontSizeInPixels = getFontSizeInPixels(
      textItem.fontSize,
      previewWidth
    );

    const lineMetrics = useTextLineHeightPx({
      fontFamily: fontFamilyName,
      fontSizePx: fontSizeInPixels,
      lineHeight: textItem.lineHeight,
      text: previewText,
      bold: textItem.bold,
      italic: textItem.italic,
      upperCase: textItem.upperCase,
    });
    const padding = useMemo(() => {
      // 0 (not undefined) so the editable TextArea doesn't fall back to antd's
      // default padding — the renderer adds no padding to backgroundless text.
      if (!hasBackground) return 0;
      if (isSubtitlesOrTitleItem) {
        return `${fontSizeInPixels * DEFAULT_RELATIVE_PADDING}px`;
      }
      return `${fontSizeInPixels * CUSTOM_TEXT_RELATIVE_PADDING}px`;
    }, [fontSizeInPixels, hasBackground, isSubtitlesOrTitleItem]);

    if (isFetched && !fontPath && !placeholderFontPath) {
      console.error("Font path not resolved for text item", {
        textItem,
        fontAssetId,
      });
      return;
    }

    const shouldWrapText = textItem.wrapText ?? true;

    const containerHeight = mapRelativeToPixels(
      textItem.size.height ?? 0,
      previewHeight
    );
    const hasVerticalAlignment = containerHeight > 0 && textItem.verticalAlign;

    // When filling the container, size to 100% so the text tracks the Moveable
    // box during a live resize; otherwise use the item's own pixel box.
    const boxWidth = fillContainer
      ? "100%"
      : mapRelativeToPixels(textItem.size.width, previewWidth);
    const boxHeight =
      containerHeight > 0
        ? fillContainer
          ? "100%"
          : containerHeight
        : "fit-content";

    const verticalAlignStyles: React.CSSProperties = hasVerticalAlignment
      ? {
          height: boxHeight,
          display: "flex",
          flexDirection: "column",
          justifyContent: VERTICAL_ALIGN_MAP[textItem.verticalAlign ?? "top"],
        }
      : {};

    // CSS leaves empty space above the first line and below the last (line-box
    // leading + the font-ascent-vs-ink gap) that the renderer doesn't. Trim it on
    // the ALIGNED edge so that edge's glyph sits flush against the box. Symmetric
    // padding comes from the box being sized to the ink height (TextEditable's
    // auto-fit) — NOT from trimming both edges, which fights flex centering /
    // flex-end. Center needs no shift (its leading is symmetric).
    const contentStyle: React.CSSProperties | undefined =
      hasVerticalAlignment && lineMetrics
        ? textItem.verticalAlign === "top"
          ? { marginTop: -lineMetrics.topInsetPx }
          : textItem.verticalAlign === "bottom"
            ? { marginBottom: -lineMetrics.bottomInsetPx }
            : undefined
        : undefined;

    return (
      <FontPreviewText
        key={textItem.fontAssetId}
        style={{
          width: boxWidth,
          height: boxHeight,
          fontSize: fontSizeInPixels,
          fontWeight: textItem.bold ? "bold" : "normal",
          fontStyle: textItem.italic ? "italic" : "normal",
          textDecoration: textItem.underline ? "underline" : "none",
          textTransform: textItem.upperCase ? "uppercase" : "none",
          backgroundColor: resolveColorToBrandkitColorIfNeeded(
            textItem.backgroundColor,
            brandKitPalette
          ),
          color: resolveColorToBrandkitColorIfNeeded(
            textItem.color,
            brandKitPalette
          ),
          WebkitTextStroke: textItem.strokeColor
            ? `0.1em ${resolveColorToBrandkitColorIfNeeded(textItem.strokeColor, brandKitPalette)}`
            : "unset",
          whiteSpace: shouldWrapText ? "normal" : "nowrap",
          //https://stackoverflow.com/questions/69253420/text-stroke-webkit-text-stroke-css-problem
          paintOrder: "stroke fill",
          // Measured value is in px; React treats `lineHeight` as unitless, so
          // a bare number would mean "N × font-size". Emit an explicit px
          // string. Falls back to the em-based unitless multiplier.
          lineHeight:
            lineMetrics != null
              ? `${lineMetrics.lineHeightPx}px`
              : 1 + textItem.lineHeight,
          padding,
          ...verticalAlignStyles,
          ...styles,
          textAlign: textItem.textAlign,
          zIndex: textItem.layerIndex,
        }}
        contentStyle={contentStyle}
        fillContainer={fillContainer}
        font={fontPath!}
        text={previewText}
        animated={"animated" in textItem ? textItem.animated : false}
        editableRef={ref as unknown as RefObject<TextAreaRef>}
        {...props}
      />
    );
  }
);

Text.displayName = "Text";
